import React from 'react';
import Button from '@components/atoms/button/button';
import Link from '@components/atoms/link/link';
import Icon from '@components/atoms/icon/icon';
import VCImage from '@components/atoms/vc-images/vc-image';
import { CartContext } from '@context/cart.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { OrderLine } from '@interfaces/models/orderLine';
import { useContextSelector } from 'use-context-selector';
import { Product } from '@interfaces/models/product';
import { useTranslation } from 'next-i18next';
import styles from './floating-cart-product.module.scss';

export interface FloatingCartProductProps {
  product: Product;
  orderLine: OrderLine;
}

const FloatingCartProduct: React.FC<FloatingCartProductProps> = (props) => {
  const { product, orderLine } = props;

  const { t: tA11y } = useTranslation('a11y');
  const { sendAnalyticEvent } = useAnalyticEvents('product');
  const removeFromShoppingCart = useContextSelector(CartContext, (v) => v.removeFromShoppingCart);

  const removeItem = async (): Promise<void> => {
    sendAnalyticEvent('shopping_bag_delete');
    await removeFromShoppingCart(orderLine?.id, product?.id);
  };

  return (
    <li className={styles.cartPreview__product}>
      <Link href={product?.path}>
        <VCImage
          className={styles.cartPreview__product__picture}
          disableLazyLoad
          src={`/produit/${product?.pictures[0]?.path}`}
          width={56}
          height={56}
          alt={product?.pictures[0]?.alt}
        />
      </Link>
      <div className={styles.cartPreview__product__description}>
        <div className={styles.cartPreview__product__line}>
          <ul className={styles.cartPreview__product__line__text}>
            <li
              className={styles.cartPreview__product__brand}
              data-cy="product_brand"
            >
              {product?.brand?.name}
            </li>
            <li
              className={styles.cartPreview__product__name}
              data-cy="product_name"
            >
              {product?.name}
            </li>
            <li data-cy="product_size">
              {product?.size?.size}&nbsp;
              <span data-cy="product_localized_standard">{product?.size?.localizedStandard}</span>
            </li>
          </ul>
          <div
            className={styles.cartPreview__product__line__amount}
            data-cy="product_price"
          >
            {product?.price?.formatted}
          </div>
        </div>
      </div>

      <Button
        disableDefaultStyling
        className={styles.cartPreview__product__remove}
        onClick={removeItem}
        data-cy="remove_product_button"
        data-vc-dd-action-name="remove_product_from_floating_cart"
        aria-label={tA11y('A11Y.ICON.REMOVE_BAG')}
      >
        <Icon
          name="cross-x-thick"
          className={styles.svg}
          ariaHidden
        />
      </Button>
    </li>
  );
};

export default FloatingCartProduct;
